import axios, { AxiosResponse, isAxiosError } from "axios";
import { put, takeLatest } from "redux-saga/effects";

import { ExcalidrawElement } from "@excalidraw/excalidraw/types/element/types";
import { BinaryFiles } from "@excalidraw/excalidraw/types/types";
import { ICreateCodingSolutionArgs } from "@/types/codingProblems.types";
import { IInitialState } from "@/types/initialState.types";
import {
  ICreateOrUpdateWhiteboardSolutionPayload,
  ICreateWhiteboardSolutionAction,
  IGetCodingSolutionAction,
  IGetWhiteboardSolutionAction,
  TCreateSolutionAPIResponse,
} from "@/types/solutions.types";
import { showToast } from "@/actions/appStatus.action";
import {
  createOrUpdateWhiteboardSolutionAPI,
  createSolutionAPI,
  getSolutionAPI,
  getSolutionByQuestionSlugAPI,
  getWhiteboardSolutionByQuestionSlugAPI,
  updateSolutionAPI,
} from "@/api/solutions.api";
import {
  CREATE_OR_UPDATE_CODING_SOLUTION,
  CREATE_OR_UPDATE_WHITEBOARD_SOLUTION,
  GET_CODING_SOLUTION,
  GET_WHITEBOARD_SOLUTION,
  SET_IS_GET_SOLUTION,
  SET_IS_GET_WHITEBOARD_SOLUTION,
  TEST_CASE_RUN_CODE,
} from "@/constants/ActionTypes";
import { HTTP_STATUS_CODES } from "@/constants/HttpConstants";
import { encodeData } from "@/utils/encodeDecode.utils";
import { getStubMeta } from "@/utils/coding.utils";
import { EToastType } from "@/components/common/AlertToast/AlertToast.types";
import { INTERVIEW_SOLUTION_CREATED } from "@/constants/SocketEventConstants";
import { ISolution } from "@/types/questions.types";
import {
  CREATE_SOLUTION_API_HTTP_STATUS_CODES,
  CREATE_SOLUTION_ERROR_MESSAGE,
  GET_SOLUTION_BY_QUESTION_SLUG_API_HTTP_STATUS_CODES,
  GET_SOLUTION_BY_QUESTION_SLUG_ERROR_MESSAGE,
  GET_WHITEBOARD_SOLUTION_BY_QUESTION_SLUG_API_HTTP_STATUS_CODES,
  GET_WHITEBOARD_SOLUTION_BY_QUESTION_SLUG_ERROR_MESSAGE,
} from "@/constants/Solutions";
import apiErrorHandler from "@/utils/apiErrorHandler";
import { call, select } from "./utils.saga";

// eslint-disable-next-line consistent-return
export function* createOrUpdateCondigSolution({
  payload,
}: ICreateCodingSolutionArgs) {
  const { isRunCode } = payload;
  try {
    const { interviewSlug, questionSlug } = payload;
    const { question, solution, socket } = yield* select(
      (state: IInitialState) => ({
        question: state.questionsDetails.questions[questionSlug],
        solution: state.solutions.allSolutions[questionSlug],
        socket: state.socket.socket,
      }),
    );
    if (question && solution) {
      const problemStubs = question?.problem?.stubs;
      const [stubLockLength, stubLockRange] = getStubMeta(
        solution.code,
        problemStubs,
        solution.technology,
      );
      const requestPayload = {
        solution_type: question.question_type,
        question_slug: questionSlug,
        technology: solution.technology,
        problem_slug: question?.problem?.slug,
        code: solution.code,
        extra_data: {
          stub_data:
            stubLockLength.head && stubLockRange.head
              ? {
                  lock_range: stubLockRange,
                  stub_range: stubLockLength,
                }
              : null,
        },
      };
      let solutionSlug = solution.slug;
      let solutionResp: AxiosResponse<TCreateSolutionAPIResponse> | undefined;
      if (!solutionSlug) {
        solutionResp = yield* call(
          createSolutionAPI,
          interviewSlug,
          requestPayload,
        );
        if (!isRunCode) {
          socket?.emit(
            INTERVIEW_SOLUTION_CREATED,
            encodeData({
              questionSlug,
            }),
          );
        }
        solutionSlug =
          "solution_slug" in solutionResp.data
            ? solutionResp.data.solution_slug
            : undefined;
      }

      if (solutionSlug) {
        solutionResp = yield* call(
          updateSolutionAPI,
          interviewSlug,
          solutionSlug,
          requestPayload,
        );
      }

      if (solutionResp) {
        if (isRunCode) return solutionResp;
        const { data } = solutionResp;
        yield put({
          type: `${CREATE_OR_UPDATE_CODING_SOLUTION}_SUCCESS`,
          payload: {
            questionSlug,
            solution: data,
          },
        });
      }
    }
  } catch (error: unknown) {
    if (isRunCode) throw error;
    const message = yield* call(
      apiErrorHandler,
      error,
      CREATE_SOLUTION_API_HTTP_STATUS_CODES,
      CREATE_SOLUTION_ERROR_MESSAGE,
    );
    if (message)
      yield put(
        showToast({
          message,
          type: EToastType.ERROR,
        }),
      );
  }
}

function* getWhiteboardSolution({ payload }: IGetWhiteboardSolutionAction) {
  try {
    const { interviewSlug, questionSlug } = payload;
    if (interviewSlug && questionSlug) {
      yield put({
        type: SET_IS_GET_WHITEBOARD_SOLUTION,
        payload: {
          isSolutionGet: false,
          questionSlug,
        },
      });
      const getWhiteboardSolutionResp = yield* call(
        getWhiteboardSolutionByQuestionSlugAPI,
        interviewSlug,
        questionSlug,
      );
      if (
        getWhiteboardSolutionResp.status === HTTP_STATUS_CODES.OK &&
        getWhiteboardSolutionResp.data.whiteboard
      ) {
        const { elements, files } = getWhiteboardSolutionResp.data.whiteboard;
        const decodedElements = window.atob(elements);
        const decodedFiles = window.atob(files);
        const elementsData = JSON.parse(
          decodedElements,
        ) as readonly ExcalidrawElement[];
        const filesData = JSON.parse(decodedFiles) as BinaryFiles;
        yield put({
          type: `${GET_WHITEBOARD_SOLUTION}_SUCCESS`,
          payload: {
            elements: elementsData,
            files: filesData,
            questionSlug,
          },
        });
      }
    }
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status !== 404) {
      const message = yield* call(
        apiErrorHandler,
        error,
        GET_WHITEBOARD_SOLUTION_BY_QUESTION_SLUG_API_HTTP_STATUS_CODES,
        GET_WHITEBOARD_SOLUTION_BY_QUESTION_SLUG_ERROR_MESSAGE,
      );
      if (message)
        yield put(
          showToast({
            message,
            type: EToastType.ERROR,
          }),
        );
    }
  } finally {
    yield put({
      type: SET_IS_GET_WHITEBOARD_SOLUTION,
      payload: {
        isSolutionGet: true,
        questionSlug: payload.questionSlug,
      },
    });
  }
}

function* createOrUpdateWhiteboardSolution({
  payload,
}: ICreateWhiteboardSolutionAction) {
  try {
    const { interviewSlug, questionSlug } = payload;
    if (interviewSlug && questionSlug) {
      const solution = yield* select(
        (state: IInitialState) =>
          state.solutions.whiteboardSolutions[questionSlug],
      );
      const elementsString = JSON.stringify(solution.elements);
      const filesString = JSON.stringify(solution.files);
      const encodedElementsString = window.btoa(elementsString);
      const encodedFilesString = window.btoa(filesString);
      const requestPayload: ICreateOrUpdateWhiteboardSolutionPayload = {
        elements: encodedElementsString,
        files: encodedFilesString,
        question_slug: questionSlug,
        solution_type: "WHB",
      };
      yield* call(
        createOrUpdateWhiteboardSolutionAPI,
        interviewSlug,
        requestPayload,
      );
    }
  } catch (error: unknown) {
    if (
      isAxiosError(error) &&
      error.response &&
      error.response.status === HTTP_STATUS_CODES.NOT_FOUND &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      error.response.data.code === "CS404"
    )
      return;
    const message = yield* call(
      apiErrorHandler,
      error,
      CREATE_SOLUTION_API_HTTP_STATUS_CODES,
      CREATE_SOLUTION_ERROR_MESSAGE,
    );
    if (message)
      yield put(
        showToast({
          message,
          type: EToastType.ERROR,
        }),
      );
  }
}

export function* getCodingSolution({ payload }: IGetCodingSolutionAction) {
  const { interviewSlug, questionSlug, isCollabSoutionGet } = payload;
  try {
    if (!isCollabSoutionGet) {
      yield put({
        type: SET_IS_GET_SOLUTION,
        payload: {
          questionSlug,
          isSolutionGet: false,
        },
      });
    }

    const solution = yield* select(
      (state: IInitialState) => state.solutions.allSolutions[questionSlug],
    );

    const testCaseResults = yield* select(
      (state: IInitialState) => state.codingProblems.testCaseResults,
    );
    let resp: AxiosResponse<ISolution>;
    if (solution?.slug) {
      resp = yield* call(getSolutionAPI, interviewSlug, solution.slug);
    } else {
      resp = yield* call(
        getSolutionByQuestionSlugAPI,
        interviewSlug,
        questionSlug,
      );
    }
    const updatedSolution = resp.data;

    if (isCollabSoutionGet) {
      updatedSolution.technology = solution.technology;
      updatedSolution.code = solution.code;
    } else if (
      updatedSolution.evaluation?.testcases &&
      updatedSolution.id &&
      testCaseResults[updatedSolution.id]?.status !== "UNE"
    ) {
      yield put({
        type: `${TEST_CASE_RUN_CODE}_RESULT`,
        payload: {
          solutionId: updatedSolution.id,
          status: updatedSolution.evaluation.status,
          testCasesResult: Object.values(updatedSolution.evaluation.testcases),
          isInitialUpate: true,
        },
      });
    }

    yield put({
      type: `${GET_CODING_SOLUTION}_SUCCESS`,
      payload: {
        questionSlug,
        solution: updatedSolution,
      },
    });
  } catch (error: unknown) {
    if (
      axios.isAxiosError(error) &&
      error.response?.status !== HTTP_STATUS_CODES.NOT_FOUND
    ) {
      const message = yield* call(
        apiErrorHandler,
        error,
        GET_SOLUTION_BY_QUESTION_SLUG_API_HTTP_STATUS_CODES,
        GET_SOLUTION_BY_QUESTION_SLUG_ERROR_MESSAGE,
      );
      if (message)
        yield put(
          showToast({
            message,
            type: EToastType.ERROR,
          }),
        );
    }
  } finally {
    yield put({
      type: SET_IS_GET_SOLUTION,
      payload: {
        questionSlug,
        isSolutionGet: true,
      },
    });
  }
}

export default function* loadSolutionsSaga() {
  yield takeLatest(
    CREATE_OR_UPDATE_CODING_SOLUTION,
    createOrUpdateCondigSolution,
  );
  yield takeLatest(GET_WHITEBOARD_SOLUTION, getWhiteboardSolution);
  yield takeLatest(
    CREATE_OR_UPDATE_WHITEBOARD_SOLUTION,
    createOrUpdateWhiteboardSolution,
  );
  yield takeLatest(GET_CODING_SOLUTION, getCodingSolution);
}
